/* global document, window, fetch, AbortController, DOMParser */

/**
 * Cart page — qty +/- and per-row Remove.
 *
 * Two-step flow on every change:
 *   1. Commit the change via WC Store API
 *      (POST /wc/store/v1/cart/update-item or /remove-item).
 *   2. Re-fetch /cart/ and DOM-swap the server-rendered `.cart-summary`
 *      + per-row line subtotals into the live page.
 *
 * Step 2 is the safety net: instead of mirroring `wc_price()` HTML and
 * every WC summary row in client code (shipping, tax, fees, coupon
 * deductions, free-shipping bars, etc.), we let the server render the
 * summary like it always does, and swap it in. One source of truth.
 *
 * Auth: the `Nonce` header carries the `wc_store_api` nonce localized
 * by app/setup.php as `window.icfStoreApi`. Same-origin cookies handle
 * session/cart association.
 *
 * Last-item removal still triggers a full reload so the @if($is_empty)
 * branch of cart.blade.php renders the empty-state card.
 */
( function () {
	const QTY_DEBOUNCE_MS = 800;
	const onReady = fn => {
		if ( document.readyState !== 'loading' ) {
			fn();
		} else {
			document.addEventListener( 'DOMContentLoaded', fn );
		}
	};

	const escapeHtml = str =>
		String( str == null ? '' : str )
			.replace( /&/g, '&amp;' )
			.replace( /</g, '&lt;' )
			.replace( />/g, '&gt;' )
			.replace( /"/g, '&quot;' )
			.replace( /'/g, '&#039;' );

	const showNotice = ( root, message, type ) => {
		const wrapper = root.querySelector( '[data-cart-notices]' );
		if ( ! wrapper ) {
			return;
		}
		const cls = type === 'error' ? 'woocommerce-error' : 'woocommerce-message';
		const el = document.createElement( 'div' );
		el.className = cls;
		el.setAttribute( 'role', 'alert' );
		// Success strings are built in this module (trusted). Error strings
		// come from the Store API — escape.
		el.innerHTML = type === 'error' ? escapeHtml( message ) : message;

		wrapper.innerHTML = '';
		wrapper.appendChild( el );

		try {
			wrapper.scrollIntoView( { behavior: 'smooth', block: 'start' } );
		} catch ( err ) {
			// older browsers — ignore
		}
	};

	const callStoreApi = async ( path, body ) => {
		const cfg = window.icfStoreApi;
		if ( ! cfg || ! cfg.root || ! cfg.nonce ) {
			throw new Error( 'Store API configuration missing' );
		}
		const res = await fetch( `${ cfg.root }${ path }`, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				Nonce: cfg.nonce,
			},
			body: JSON.stringify( body ),
		} );

		const data = await res.json().catch( () => null );

		if ( ! res.ok ) {
			const msg =
				( data && ( data.message || ( data.data && data.data.message ) ) ) ||
				`HTTP ${ res.status }`;
			const err = new Error( msg );
			err.response = data;
			throw err;
		}
		return data;
	};

	/**
	 * Show / hide the small "Updating…" pill next to a row's qty control
	 * while the change is queued or in flight.
	 */
	const setRowPending = ( row, pending ) => {
		if ( ! row ) {
			return;
		}
		let pill = row.querySelector( '[data-qty-pending]' );
		if ( pending ) {
			if ( ! pill ) {
				pill = document.createElement( 'span' );
				pill.className = 'cart-item__qty-pending';
				pill.setAttribute( 'data-qty-pending', '' );
				pill.setAttribute( 'aria-live', 'polite' );
				pill.textContent = 'Updating…';
				const qtyWrap = row.querySelector( '.cart-item__qty' ) || row;
				qtyWrap.appendChild( pill );
			}
		} else if ( pill ) {
			pill.remove();
		}
	};

	/**
	 * Re-fetch /cart/ and swap the server-rendered .cart-summary + row
	 * line subtotals into the live page. Uses AbortController so a newer
	 * change cancels a stale in-flight refresh ("newest wins").
	 *
	 * If focus is inside the coupon form, we preserve the typed value so
	 * the user's mid-typing doesn't get wiped by the swap.
	 */
	const createRefresher = root => {
		let inFlight = null;
		const cartUrl = ( window.icfStoreApi && window.icfStoreApi.cartUrl ) || '/cart/';

		return async () => {
			if ( inFlight ) {
				inFlight.abort();
			}
			const controller = new AbortController();
			inFlight = controller;

			const summaryEl = root.querySelector( '[data-cart-summary-region]' );
			if ( summaryEl ) {
				summaryEl.classList.add( 'cart-summary--refreshing' );
			}

			let html;
			try {
				const res = await fetch( cartUrl, {
					credentials: 'same-origin',
					signal: controller.signal,
					headers: { Accept: 'text/html' },
				} );
				if ( ! res.ok ) {
					throw new Error( `HTTP ${ res.status }` );
				}
				html = await res.text();
			} catch ( err ) {
				if ( err.name === 'AbortError' ) {
					return;
				}
				if ( summaryEl ) {
					summaryEl.classList.remove( 'cart-summary--refreshing' );
				}
				showNotice(
					root,
					'Could not refresh totals — refresh the page to see the latest.',
					'error'
				);
				if ( inFlight === controller ) {
					inFlight = null;
				}
				return;
			}

			// A newer refresh started while we were fetching — discard.
			if ( inFlight !== controller ) {
				return;
			}
			inFlight = null;

			const parsed = new DOMParser().parseFromString( html, 'text/html' );

			// Swap the summary card.
			const fresh = parsed.querySelector( '[data-cart-summary-region]' );
			if ( fresh && summaryEl ) {
				// Focus-aware: if the user is mid-typing in the coupon
				// input inside the live summary, mirror the typed value
				// onto the replacement before the swap so their input
				// survives.
				const liveCoupon = summaryEl.querySelector( '#coupon_code' );
				const live = document.activeElement;
				if ( liveCoupon && live === liveCoupon ) {
					const freshCoupon = fresh.querySelector( '#coupon_code' );
					if ( freshCoupon ) {
						freshCoupon.value = liveCoupon.value;
					}
				}

				fresh.classList.remove( 'cart-summary--refreshing' );
				summaryEl.replaceWith( fresh );

				// Restore focus + caret if the user was typing in the
				// coupon input, since replaceWith blurs the old node.
				if ( liveCoupon && live === liveCoupon ) {
					const restored = fresh.querySelector( '#coupon_code' );
					if ( restored ) {
						const pos = liveCoupon.selectionStart;
						restored.focus();
						try {
							restored.setSelectionRange( pos, pos );
						} catch ( err ) {
							// some input types reject selection — ignore
						}
					}
				}
			} else if ( summaryEl ) {
				summaryEl.classList.remove( 'cart-summary--refreshing' );
			}

			// Patch any per-row line subtotals from the fresh markup.
			parsed.querySelectorAll( '.cart-item[data-cart-item-key]' ).forEach( freshRow => {
				const key = freshRow.getAttribute( 'data-cart-item-key' );
				const liveRow = root.querySelector(
					`.cart-item[data-cart-item-key="${ CSS.escape( key ) }"]`
				);
				if ( ! liveRow ) {
					return;
				}
				const freshPrice = freshRow.querySelector( '.cart-item__price' );
				const livePrice = liveRow.querySelector( '.cart-item__price' );
				if ( freshPrice && livePrice ) {
					livePrice.innerHTML = freshPrice.innerHTML;
				}
			} );
		};
	};

	const init = () => {
		const root = document.querySelector( '[data-cart-page]' );
		console.log( 'hello init', root );
		if ( ! root ) {
			return;
		}
		if ( ! window.icfStoreApi || ! window.icfStoreApi.root ) {
			// eslint-disable-next-line no-console
			console.warn( '[cart] window.icfStoreApi missing — falling back to no-op.' );
			return;
		}

		const refreshCartSummary = createRefresher( root );

		// Per-row debounce so rapid clicks on row A don't cancel a
		// pending update on row B.
		const debounceTimers = new Map();
		// Per-row in-flight lock so a slow network can't be raced by a
		// second commit landing before the first response.
		const inFlight = new Map();
		// Last server-confirmed qty per row so we can revert the input
		// if the API rejects an update.
		const lastQty = new Map();

		const commitItem = async ( key, quantity, row, inputEl ) => {
			if ( inFlight.has( key ) ) {
				return;
			}
			inFlight.set( key, true );
			setRowPending( row, true );
			try {
				await callStoreApi( '/cart/update-item', { key, quantity } );
				lastQty.set( key, quantity );
				await refreshCartSummary();
			} catch ( err ) {
				if ( inputEl && lastQty.has( key ) ) {
					inputEl.value = lastQty.get( key );
				}
				showNotice( root, err.message || 'Could not update cart.', 'error' );
			} finally {
				inFlight.delete( key );
				setRowPending( row, false );
			}
		};

		const removeItem = async ( key, name, rowEl ) => {
			if ( inFlight.has( key ) ) {
				return;
			}
			inFlight.set( key, true );
			if ( rowEl ) {
				rowEl.classList.add( 'is-removing' );
			}
			try {
				const cart = await callStoreApi( '/cart/remove-item', { key } );
				const itemsCount = Number.isFinite( cart && cart.items_count )
					? cart.items_count
					: 0;

				if ( itemsCount === 0 ) {
					window.location.reload();
					return;
				}

				if ( rowEl ) {
					rowEl.remove();
				}
				await refreshCartSummary();
				showNotice(
					root,
					`&ldquo;${ escapeHtml( name || 'Item' ) }&rdquo; removed.`,
					'success'
				);
			} catch ( err ) {
				if ( rowEl ) {
					rowEl.classList.remove( 'is-removing' );
				}
				showNotice( root, err.message || 'Could not remove item.', 'error' );
			} finally {
				inFlight.delete( key );
			}
		};

		// Seed lastQty from rendered inputs so the revert-on-error path
		// has a value to fall back to before the first update lands.
		root.querySelectorAll( '.cart-item[data-cart-item-key]' ).forEach( row => {
			const key = row.getAttribute( 'data-cart-item-key' );
			const input = row.querySelector( 'input.qty' );
			if ( key && input ) {
				const v = parseInt( input.value, 10 );
				if ( Number.isFinite( v ) ) {
					lastQty.set( key, v );
				}
			}
		} );

		// Qty input change (via +/- buttons in single-product.js or direct
		// typing). Debounced per-row so rapid bursts coalesce to one
		// commit + one refetch.
		root.addEventListener( 'change', e => {
			const input =
				e.target &&
				e.target.matches &&
				e.target.matches( 'input.qty, input.qty-control__input' )
					? e.target
					: null;
			if ( ! input ) {
				return;
			}
			const row = input.closest( '.cart-item[data-cart-item-key]' );
			if ( ! row ) {
				return;
			}
			const key = row.getAttribute( 'data-cart-item-key' );
			const qty = parseInt( input.value, 10 );
			if ( ! Number.isFinite( qty ) || qty < 0 ) {
				return;
			}

			// qty=0 from the +/- buttons is "remove" per WC convention.
			if ( qty === 0 ) {
				const name = row.getAttribute( 'data-product-name' ) || '';
				removeItem( key, name, row );
				return;
			}

			setRowPending( row, true );

			if ( debounceTimers.has( key ) ) {
				window.clearTimeout( debounceTimers.get( key ) );
			}
			debounceTimers.set(
				key,
				window.setTimeout( () => {
					debounceTimers.delete( key );
					commitItem( key, qty, row, input );
				}, QTY_DEBOUNCE_MS )
			);
		} );

		// Per-row Remove link — hijack the click.
		root.addEventListener( 'click', e => {
			const removeLink = e.target.closest( '.cart-item__remove' );
			if ( ! removeLink ) {
				return;
			}
			e.preventDefault();
			const row = removeLink.closest( '.cart-item[data-cart-item-key]' );
			if ( ! row ) {
				return;
			}
			const key = row.getAttribute( 'data-cart-item-key' );
			const name = row.getAttribute( 'data-product-name' ) || '';
			if ( key ) {
				removeItem( key, name, row );
			}
		} );
	};

	onReady( init );
} )();
